import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  Calendar,
  Building2,
  Target,
  ArrowUpRight,
  ArrowDownRight
} from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { superAdminService } from '../../utils/services/superAdminService';

interface RevenueMetrics {
  totalMRR: number;
  totalARR: number;
  growth: number;
  churnRate: number;
  avgRevenuePerTenant: number;
  projectedAnnual: number;
}

export default function RevenueAnalytics() {
  const [metrics, setMetrics] = useState<RevenueMetrics>({
    totalMRR: 0,
    totalARR: 0,
    growth: 0,
    churnRate: 0,
    avgRevenuePerTenant: 0,
    projectedAnnual: 0,
  });

  const [loading, setLoading] = useState(true);

  // Empty data for charts - will be populated from real data later
  const monthlyRevenueData: any[] = [];
  const revenueByCompanySize: any[] = [];
  const topRevenueCompanies: any[] = [];
  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444'];

  useEffect(() => {
    loadRevenueData();
  }, []);

  const loadRevenueData = async () => {
    try {
      setLoading(true);
      const metrics = await superAdminService.getRevenueMetrics();

      setMetrics({
        totalMRR: metrics.mrr,
        totalARR: metrics.arr,
        growth: metrics.growthRate,
        churnRate: 2.1,
        avgRevenuePerTenant: metrics.avgRevenuePerTenant,
        projectedAnnual: metrics.arr * 1.15,
      });
    } catch (error) {
      console.error('❌ Error loading revenue data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercent = (value: number) => {
    return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl flex items-center gap-2">
          <DollarSign className="h-6 w-6 text-green-600" />
          Revenue Analytics
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          Track revenue, growth, and financial projections
        </p>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2 text-gray-600">
              <DollarSign className="h-4 w-4 text-green-600" />
              Monthly Recurring Revenue
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl">{formatCurrency(metrics.totalMRR)}</span>
            </div>
            <div className="flex items-center gap-1 mt-2">
              {metrics.growth >= 0 ? (
                <>
                  <ArrowUpRight className="h-4 w-4 text-green-600" />
                  <span className="text-sm text-green-600">{formatPercent(metrics.growth)}</span>
                </>
              ) : (
                <>
                  <ArrowDownRight className="h-4 w-4 text-red-600" />
                  <span className="text-sm text-red-600">{formatPercent(metrics.growth)}</span>
                </>
              )}
              <span className="text-sm text-gray-500 ml-1">vs last month</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2 text-gray-600">
              <Calendar className="h-4 w-4 text-blue-600" />
              Annual Recurring Revenue
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl">{formatCurrency(metrics.totalARR)}</span>
            </div>
            <p className="text-sm text-gray-500 mt-2">
              Current run rate
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2 text-gray-600">
              <Target className="h-4 w-4 text-purple-600" />
              Projected Annual
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl">{formatCurrency(metrics.projectedAnnual)}</span>
            </div>
            <p className="text-sm text-gray-500 mt-2">
              Based on current growth
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2 text-gray-600">
              <Building2 className="h-4 w-4 text-orange-600" />
              Avg Revenue per Tenant
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl">{formatCurrency(metrics.avgRevenuePerTenant)}</span>
            </div>
            <p className="text-sm text-gray-500 mt-2">
              Per month
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2 text-gray-600">
              <TrendingUp className="h-4 w-4 text-green-600" />
              Growth Rate
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl">{formatPercent(metrics.growth)}</span>
            </div>
            <p className="text-sm text-gray-500 mt-2">
              Month over month
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2 text-gray-600">
              <TrendingDown className="h-4 w-4 text-red-600" />
              Churn Rate
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl">{metrics.churnRate.toFixed(1)}%</span>
            </div>
            <p className="text-sm text-gray-500 mt-2">
              {metrics.churnRate < 3 ? 'Healthy' : 'Needs attention'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Revenue Trend Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-green-600" />
            Revenue Growth Trend
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={monthlyRevenueData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip
                formatter={(value: any) => formatCurrency(value)}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="revenue"
                stroke="#10B981"
                strokeWidth={2}
                dot={{ r: 4 }}
                name="Monthly Revenue"
              />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Revenue by Company Size */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Revenue by Company Size</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={revenueByCompanySize}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {revenueByCompanySize.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value: any) => formatCurrency(value)} />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm">Wells by Company Size</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={revenueByCompanySize}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" fill="#3B82F6" name="Companies" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Top Revenue Companies */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5 text-blue-600" />
            Top Revenue Companies
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {topRevenueCompanies.map((company, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center gap-4">
                  <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center font-medium">
                    {index + 1}
                  </div>
                  <div>
                    <p className="font-medium">{company.company}</p>
                    <p className="text-sm text-gray-500">{company.wells} wells</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-medium">{formatCurrency(company.mrr)}/mo</p>
                  <div className="flex items-center gap-1 mt-1">
                    {company.growth >= 0 ? (
                      <>
                        <TrendingUp className="h-3 w-3 text-green-600" />
                        <span className="text-sm text-green-600">{formatPercent(company.growth)}</span>
                      </>
                    ) : (
                      <>
                        <TrendingDown className="h-3 w-3 text-red-600" />
                        <span className="text-sm text-red-600">{formatPercent(company.growth)}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}